<script setup lang="ts">
import { useProcessFlowStore } from '@Portal/stores/processflow';
import CleanedField from '@Shared/components/CleanedField.vue';
import NpsDetractor from '@Shared/components/visuals/visualAssets/NpsDetractor.svg?component';
import NpsEmpty from '@Shared/components/visuals/visualAssets/NpsEmpty.svg?component';
import NpsNeutral from '@Shared/components/visuals/visualAssets/NpsNeutral.svg?component';
import NpsPromoter from '@Shared/components/visuals/visualAssets/NpsPromoter.svg?component';
import { ProcessFlowCard } from '@Shared/types/portal/ProcessFlow';
import dayjs from 'dayjs';

import isoWeek from 'dayjs/plugin/isoWeek';
import isToday from 'dayjs/plugin/isToday';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(isoWeek);
dayjs.extend(isToday);
dayjs.extend(relativeTime);

const emit = defineEmits<{
  (event: 'cardClick', card: ProcessFlowCard): void;
}>();
const processFlowStore = useProcessFlowStore();

const props = defineProps<{
  card: ProcessFlowCard;
  smartDataModuleId: string;
}>();

const { card, smartDataModuleId } = toRefs(props);

const primaryMetricIcon = (card: ProcessFlowCard) => {
  if (card.primaryMetric.index == 1) return NpsPromoter;
  if (card.primaryMetric.index == 0) return NpsNeutral;
  if (card.primaryMetric.index == -1) return NpsDetractor;
  if (card.primaryMetric.index == null) return NpsEmpty;
  return null;
};

const calculateRelativeTime = (card: ProcessFlowCard) => {
  if (!card.responded.length || !card.responded[0]) return '';

  const date = dayjs(card.responded[0].value);
  return date.from(dayjs(import.meta.env.VITE_DATE_ANCHOR));
};

const verbatim = ref<Element>();
const verbatimIsTruncated = ref<boolean>(false);
const showingMore = ref<boolean>(false);
const isDisplayingRawValue = ref<boolean>(false);

onMounted(() => {
  // We want to detect if a card's verbatim is too large to fit (so we can show a 'show more' button)
  // if the scrollHeight ("true height"/that can be scrolled) is greater than the visible height of the box
  // then we have something that's too large.
  const observer = new ResizeObserver(function (entries) {
    for (const entry of entries) {
      const el = entry.target.querySelector('a');
      verbatimIsTruncated.value = !!el && el.scrollHeight > entry.contentRect.height + 2; // sometimes browser will add 1px to scrollHeight;
    }
  });

  observer.observe(verbatim.value!);
});
</script>
<template>
  <div
    class="border-t-4 border-solid rounded-1 border-[#CFD6E2] border-1 font-family[Roboto]"
    :class="{
      'border-t-[#7F7466]': card.primaryMetric.index == 0,
      'border-t-[#D4002B]': card.primaryMetric.index == -1,
      'border-t-[#0C8A65]': card.primaryMetric.index == 1,
      'border-t-[#C5CDDC]': card.primaryMetric.index == null,
    }"
    :style="{
      boxShadow: processFlowStore.currentCard.id === card.id ? '0px 4px 4px rgba(0, 0, 0, 0.16)' : 'none',
    }"
  >
    <div class="flex flex-row py-4 pl-4 border-0 border-b-1 border-solid border-[#CFD6E2]">
      <component :is="primaryMetricIcon(card)" class="mt-auto mb-auto min-w-24px h-24px" />
      <div class="flex flex-col pl-3 max-w-50% mr-auto">
        <div class="color-[#4F4F4F] text-3 mobile:text-4 font-semibold truncate">
          {{ card.contact.customerName?.value }}
        </div>
        <div class="color-[#828282] text-3 mobile:text-4 flex flex-row justify-between">
          <div class="truncate">{{ card.program }}</div>
          <div class="pl-2 whitespace-nowrap">
            {{ calculateRelativeTime(card) }}
          </div>
        </div>
      </div>
      <div class="flex flex-row items-center h-24px max-w-45%">
        <svg
          :class="{
            'fill-[#EAF6FF]': card.processFlow.isTerminatingState,
            'fill-[#F4F6FB]': !card.processFlow.isTerminatingState,
          }"
          class="min-w-9px h-24px ml-auto"
          viewBox="0 0 9 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M9 0H1.3927C0.0839375 0 -0.499334 2.18278 0.516198 3.27885L7.26936 10.567C7.95411 11.3057 7.95411 12.6939 7.26936 13.4326L0.516198 20.7208C-0.499334 21.8168 0.0839375 23.9996 1.3927 23.9996H9V0Z"
          />
        </svg>
        <div
          :class="[
            {
              'bg-[#EAF6FF] text-[#2689D1] border-[#2689D1]': card.processFlow.isTerminatingState,
              'bg-[#F4F6FB] border-[#4F4F4F]': !card.processFlow.isTerminatingState,
            },
          ]"
          class="w-full flex flex-row items-center gap-1 px-1.5 line-height-24px h-24px border-0 border-r-1 border-solid"
        >
          <div
            class="truncate uppercase text-2.5 mobile:text-4"
            :class="{
              'color-[#2689D1]': card.processFlow.isTerminatingState,
              'color-[#4F4F4F]': !card.processFlow.isTerminatingState,
            }"
          >
            {{ card.processFlow.state }}
          </div>
          <div
            v-if="card.processFlow.isTerminatingState"
            class="text-3 mobile:text-4 i-mdi-checkbox-marked-circle"
          ></div>
        </div>
      </div>
    </div>
    <div class="px-4 color-[#4F4F4F] text-3 mobile:text-4 pt-3 pb-4">
      <div
        ref="verbatim"
        :class="{
          italic: !card.summaryVerbatim?.value || card.summaryVerbatim?.value == '-',
        }"
        v-if="card.summaryVerbatim != null"
      >
        <CleanedField
          :record-id="card.id"
          :empty-value-text="'No comment provided'"
          :text-styling="
            !showingMore && isDisplayingRawValue
              ? {
                  overflow: 'hidden',
                  display: '-webkit-box',
                  '-webkit-box-orient': 'vertical',
                  '-webkit-line-clamp': 2,
                  'line-clamp': 2,
                }
              : {}
          "
          :smart-data-module-id="smartDataModuleId"
          v-model="isDisplayingRawValue"
          :value="card.summaryVerbatim!.value"
          :is-field-cleaned="card.summaryVerbatim!.isCleaned"
          :key-field="card.summaryVerbatim!.key"
        ></CleanedField>
      </div>
      <div
        :id="`processflow-verbatim-expand-${card.id}`"
        class="cursor-pointer flex flex-row pt-1 underline"
        @click="showingMore = !showingMore"
        v-if="(verbatimIsTruncated && isDisplayingRawValue) || showingMore"
      >
        Show {{ showingMore ? 'Less' : 'More' }}
      </div>

      <div
        :id="`processflow-view-details-${card.id}`"
        @click="emit('cardClick', card)"
        class="color-[#C73362] mt-4 flex flex-row items-center cursor-pointer select-none"
      >
        View Details
        <div class="ml-1 i-mdi-chevron-right-circle text-3.5" />
      </div>
    </div>
  </div>
</template>
